// src/utils/activityReportSync.ts
// Utility to sync activities from the activity panel to report form fields

export interface ActivityMapping {
  section: string;
  field: string;
  fieldLabel: string;
}

export interface ReportUpdate {
  section: string;
  field: string;
  fieldLabel: string;
  value: string;
  isAppend: boolean; // true if value should be appended, false if replaced
}

// Keywords/patterns that map to specific report fields
// Format: { pattern: regex or string, mapping: { section, field, fieldLabel }, extractValue?: function }
interface PatternMapping {
  patterns: RegExp[];
  mapping: ActivityMapping;
  extractValue?: (text: string, match: RegExpMatchArray) => string;
  isAppendField: boolean; // Fields like "events" should append, vitals should replace
}

const activityPatterns: PatternMapping[] = [
  // Events/Falls
  {
    patterns: [/\bfall\b/i, /\bfell\b/i, /patient fell/i, /had a fall/i],
    mapping: { section: 'additionalNotes', field: 'events', fieldLabel: 'Events' },
    isAppendField: true
  },
  {
    patterns: [/\bseizure\b/i, /had a seizure/i, /seizure activity/i],
    mapping: { section: 'additionalNotes', field: 'events', fieldLabel: 'Events' },
    isAppendField: true
  },
  {
    patterns: [/code blue/i, /rapid response/i, /emergency/i],
    mapping: { section: 'additionalNotes', field: 'events', fieldLabel: 'Events' },
    isAppendField: true
  },
  
  // Vitals - Heart Rate
  {
    patterns: [/\bhr\s*[:=]?\s*(\d+)/i, /heart rate\s*[:=]?\s*(\d+)/i, /pulse\s*[:=]?\s*(\d+)/i, /heart rate (?:is |was |of )?(\d+)/i],
    mapping: { section: 'vitalsOxygen', field: 'hr', fieldLabel: 'Heart Rate' },
    extractValue: (text, match) => match[1] || '',
    isAppendField: false
  },
  
  // Vitals - Blood Pressure
  {
    patterns: [/\bbp\s*[:=]?\s*(\d+\/\d+)/i, /blood pressure\s*[:=]?\s*(\d+\/\d+)/i, /bp (?:is |was |of )?(\d+\/\d+)/i],
    mapping: { section: 'vitalsOxygen', field: 'bp', fieldLabel: 'Blood Pressure' },
    extractValue: (text, match) => match[1] || '',
    isAppendField: false
  },
  
  // Vitals - Temperature
  {
    patterns: [/\btemp\s*[:=]?\s*([\d.]+)/i, /temperature\s*[:=]?\s*([\d.]+)/i, /temp (?:is |was |of )?([\d.]+)/i, /fever of ([\d.]+)/i],
    mapping: { section: 'vitalsOxygen', field: 'temp', fieldLabel: 'Temperature' },
    extractValue: (text, match) => match[1] || '',
    isAppendField: false
  },
  
  // Vitals - Oxygen/SpO2
  {
    patterns: [/\bspo2\s*[:=]?\s*(\d+)/i, /\bo2 sat\s*[:=]?\s*(\d+)/i, /oxygen\s*[:=]?\s*(\d+)/i, /sats? (?:is |was |at |of )?(\d+)/i],
    mapping: { section: 'vitalsOxygen', field: 'oxygen', fieldLabel: 'Oxygen' },
    extractValue: (text, match) => match[1] + '%',
    isAppendField: false
  },
  
  // Vitals - Respiratory Rate
  {
    patterns: [/\brr\s*[:=]?\s*(\d+)/i, /respiratory rate\s*[:=]?\s*(\d+)/i, /resp rate\s*[:=]?\s*(\d+)/i],
    mapping: { section: 'icuVitals', field: 'rr', fieldLabel: 'Respiratory Rate' },
    extractValue: (text, match) => match[1] || '',
    isAppendField: false
  },
  
  // Pain Score
  {
    patterns: [/pain\s*(?:score|level)?\s*[:=]?\s*(\d+)/i, /pain (?:is |was |at |of )?(\d+)/i, /(\d+)\/10 pain/i],
    mapping: { section: 'painManagement', field: 'painScore', fieldLabel: 'Pain Score' },
    extractValue: (text, match) => match[1] + '/10',
    isAppendField: false
  },
  
  // Medications given
  {
    patterns: [/gave\s+(.+?(?:mg|mcg|ml|units?))/i, /administered\s+(.+?(?:mg|mcg|ml|units?))/i, /prn\s+(.+?(?:mg|mcg|ml|units?))/i],
    mapping: { section: 'medications', field: 'prnMedsGiven', fieldLabel: 'PRN Meds Given' },
    extractValue: (text, match) => match[1] || text,
    isAppendField: true
  },
  
  // Skin/Wound
  {
    patterns: [/skin\s+(?:is |appears? )?(intact|broken|reddened|bruised)/i, /wound\s+(.+)/i, /pressure\s+(?:ulcer|injury|sore)/i],
    mapping: { section: 'mobilitySkin', field: 'skin', fieldLabel: 'Skin' },
    isAppendField: true
  },
  
  // Activity/Mobility
  {
    patterns: [/ambulated\s+(.+)/i, /walked\s+(.+)/i, /out of bed/i, /oob/i, /bed rest/i, /chair/i],
    mapping: { section: 'mobilitySkin', field: 'activity', fieldLabel: 'Activity' },
    isAppendField: true
  },
  
  // Labs
  {
    patterns: [/lab(?:s)?\s*[:=]?\s*(.+)/i, /glucose\s*[:=]?\s*(\d+)/i, /potassium\s*[:=]?\s*([\d.]+)/i, /hemoglobin\s*[:=]?\s*([\d.]+)/i],
    mapping: { section: 'labsTests', field: 'latestLabs', fieldLabel: 'Latest Labs' },
    isAppendField: true
  },
  
  // Intake/Output
  {
    patterns: [/intake\s*[:=]?\s*(\d+)/i, /output\s*[:=]?\s*(\d+)/i, /i\/o\s*[:=]?\s*(.+)/i, /voided\s*(\d+)?/i, /urine output/i],
    mapping: { section: 'eliminationNutrition', field: 'bladderProgram', fieldLabel: 'Bladder Program' },
    isAppendField: true
  },
  
  // Diet/Nutrition
  {
    patterns: [/ate\s+(.+)/i, /diet\s+(.+)/i, /npo/i, /clear liquids/i, /regular diet/i, /tolerated\s+(.+)/i],
    mapping: { section: 'eliminationNutrition', field: 'dietType', fieldLabel: 'Diet Type' },
    isAppendField: true
  },
  
  // Family/Consults
  {
    patterns: [/family\s+(.+)/i, /spoke with family/i, /family meeting/i, /consult\s+(.+)/i],
    mapping: { section: 'additionalNotes', field: 'familyConsults', fieldLabel: 'Family/Consults' },
    isAppendField: true
  },
  
  // Provider updates
  {
    patterns: [/(?:dr|doctor|provider|physician)\s+(.+)/i, /notified\s+(?:dr|doctor|provider)/i, /called\s+(?:dr|doctor|provider)/i],
    mapping: { section: 'notes', field: 'providerUpdates', fieldLabel: 'Provider Updates' },
    isAppendField: true
  },
  
  // Discharge planning
  {
    patterns: [/discharge\s+(.+)/i, /going home/i, /d\/c planning/i],
    mapping: { section: 'dischargePlanning', field: 'dischargeGoal', fieldLabel: 'Discharge Goal' },
    isAppendField: true
  }
];

// Department-specific field mappings (some departments have different section names)
const departmentFieldMappings: Record<string, Record<string, { section: string; field: string }>> = {
  'ICU': {
    'hr': { section: 'icuVitals', field: 'rr' }, // ICU uses icuVitals
    'events': { section: 'additionalNotes', field: 'familyConsults' }
  },
  'PACU': {
    'hr': { section: 'circulation', field: 'hr' },
    'bp': { section: 'circulation', field: 'bp' },
    'events': { section: 'additionalNotes', field: 'events' }
  },
  'ER': {
    'events': { section: 'additionalNotes', field: 'events' }
  },
  'Rehab': {
    'events': { section: 'additionalNotes', field: 'events' },
    'painScore': { section: 'painSpasticity', field: 'painScore' }
  }
};

/**
 * Parse an activity text and extract report field updates
 */
export function parseActivityForReportUpdates(
  activityText: string,
  department: string = 'Med-Surg'
): ReportUpdate[] {
  const updates: ReportUpdate[] = [];
  const normalizedText = activityText.toLowerCase();
  
  for (const patternDef of activityPatterns) {
    for (const pattern of patternDef.patterns) {
      const match = activityText.match(pattern);
      if (match) {
        let { section, field, fieldLabel } = patternDef.mapping;
        
        // Check for department-specific field mapping
        const deptMapping = departmentFieldMappings[department]?.[field];
        if (deptMapping) {
          section = deptMapping.section;
          field = deptMapping.field;
        }
        
        // Extract value
        let value = activityText;
        if (patternDef.extractValue && match) {
          value = patternDef.extractValue(activityText, match);
        }
        
        // Avoid duplicates
        const existingUpdate = updates.find(u => u.section === section && u.field === field);
        if (!existingUpdate) {
          updates.push({
            section,
            field,
            fieldLabel,
            value,
            isAppend: patternDef.isAppendField
          });
        }
        
        break; // Only match first pattern per definition
      }
    }
  }
  
  return updates;
}

/**
 * Apply updates to a report object
 */
export function applyUpdatesToReport(
  currentReport: Record<string, Record<string, string>>,
  updates: ReportUpdate[]
): { updatedReport: Record<string, Record<string, string>>; changedFields: ReportUpdate[] } {
  const updatedReport = JSON.parse(JSON.stringify(currentReport)); // Deep clone
  const changedFields: ReportUpdate[] = [];
  
  for (const update of updates) {
    if (!updatedReport[update.section]) {
      updatedReport[update.section] = {};
    }
    
    const currentValue = updatedReport[update.section][update.field] || '';
    let newValue: string;
    
    if (update.isAppend) {
      // Append to existing value with comma separator
      if (currentValue) {
        // Check if value already exists to avoid duplicates
        if (!currentValue.toLowerCase().includes(update.value.toLowerCase())) {
          newValue = currentValue + ', ' + update.value;
        } else {
          continue; // Skip if already exists
        }
      } else {
        newValue = update.value;
      }
    } else {
      // Replace value (for vitals, etc.)
      if (currentValue !== update.value) {
        newValue = update.value;
      } else {
        continue; // Skip if same value
      }
    }
    
    updatedReport[update.section][update.field] = newValue;
    changedFields.push({
      ...update,
      value: newValue
    });
  }
  
  return { updatedReport, changedFields };
}

/**
 * Get a human-readable description of what field was updated
 */
export function getUpdateDescription(update: ReportUpdate): string {
  if (update.isAppend) {
    return `Added "${update.value}" to ${update.fieldLabel}`;
  } else {
    return `${update.fieldLabel} updated to ${update.value}`;
  }
}
